import { Autocomplete, IconButton, TextField, Tooltip } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { KeyboardEvent, memo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import useFetch from "../../../hooks/useFetch";
import {
  getInternalProcedures,
  getTasyInternalProcedures,
} from "../../../services/internalProcedure.service";
import { IInternalProcedure } from "../../../models/internal-procedure";
import { tokenService } from "../../../services/token.service";

const translationPath = "components.procedureSearch.";

const MIN_SEARCH_LENGTH = 3;

interface IProcedureSearchProps {
  value: IInternalProcedure | null;
  onChange: (procedure: IInternalProcedure | null) => void;
}

const ProcedureSearch: React.FC<IProcedureSearchProps> = ({
  value,
  onChange,
}) => {
  const { t } = useTranslation();

  const [inputValue, setInputValue] = useState("");

  const [options, setOptions] = useState<IInternalProcedure[]>([]);

  const [open, setOpen] = useState(false);

  const isLogged = !!tokenService.hasAccessTokens();

  const { sendRequest, loading } = useFetch(
    isLogged ? getInternalProcedures : getTasyInternalProcedures
  );

  const searchHandler = useCallback(async () => {
    const searchBy = inputValue.trim();

    if (searchBy.length < MIN_SEARCH_LENGTH) return;

    const { data, success } = await sendRequest({ SearchBy: searchBy });

    if (data && success) {
      setOptions(data);
      setOpen(true);
    }
  }, [inputValue, sendRequest]);

  const keyDownHandler = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();
        searchHandler();
      }
    },
    [searchHandler]
  );

  const changeHandler = useCallback(
    (procedure: IInternalProcedure | null) => {
      onChange(procedure);
      setInputValue(procedure?.procedureExamName ?? "");
      if (!procedure) {
        setOptions([]);
      }
    },
    [onChange]
  );

  return (
    <Autocomplete
      sx={{ minWidth: 450 }}
      size="small"
      value={value}
      options={options}
      loading={loading}
      open={open}
      onOpen={() => setOpen(options.length > 0)}
      onClose={() => setOpen(false)}
      filterOptions={(option) => option}
      noOptionsText={t(translationPath + "no_options")}
      getOptionLabel={(option) => option.procedureExamName ?? ""}
      isOptionEqualToValue={(option, selected) =>
        option.identifier === selected.identifier
      }
      onChange={(_, newValue) => changeHandler(newValue)}
      inputValue={inputValue}
      onInputChange={(_, newInputValue, reason) => {
        if (reason !== "reset") {
          setInputValue(newInputValue ?? "");
        }
      }}
      clearIcon={<ClearIcon fontSize="small" />}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t(translationPath + "procedure")}
          onKeyDown={keyDownHandler}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                <Tooltip title={t(translationPath + "search") || "Pesquisar"}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={searchHandler}
                      disabled={inputValue.trim().length < MIN_SEARCH_LENGTH}
                      edge="end"
                    >
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default memo(ProcedureSearch);
